import React, { useState, useEffect } from "react";
import axios from "../../../utils/axios";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { Checkbox } from "../../../components/ui/Checkbox";
import SoftwareCategoryTab from "./SoftwareCategoryTab";

const AssetCategoriesTab = () => {
  const [activeTab, setActiveTab] = useState("hardware");

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: "",
    code: "",
    description: "",
    fields: [],
    is_active: true,
  });
  const [newField, setNewField] = useState("");

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get("/asset-categories");

      if (response.data?.success) {
        setCategories(
          Array.isArray(response.data.data) ? response.data.data : []
        );
      } else {
        setCategories([]);
        setError("Unable to load hardware asset types.");
      }
    } catch (err) {
      console.error("Error fetching hardware categories:", err);
      setError(
        err?.response?.data?.message ||
          "Unable to load hardware asset types."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const closeModal = () => {
    setShowAddModal(false);
    setEditingCategory(null);
    setNewCategory({
      name: "",
      code: "",
      description: "",
      fields: [],
      is_active: true,
    });
    setNewField("");
  };

  const handleAddCategory = async () => {
    try {
      const response = await axios.post("/asset-categories", newCategory);

      if (response.data?.success) {
        await fetchCategories();
        closeModal();
      }
    } catch (err) {
      console.error(err);
      alert(
        err?.response?.data?.message || "Failed to create hardware category."
      );
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategory({
      name: category.name || "",
      code: category.code || "",
      description: category.description || "",
      fields: Array.isArray(category.fields) ? category.fields : [],
      is_active: Boolean(category.is_active),
    });
    setShowAddModal(true);
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;

    try {
      const response = await axios.put(
        `/asset-categories/${editingCategory.id}`,
        newCategory
      );

      if (response.data?.success) {
        await fetchCategories();
        closeModal();
      }
    } catch (err) {
      console.error(err);
      alert(
        err?.response?.data?.message || "Failed to update hardware category."
      );
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) {
      return;
    }

    try {
      const response = await axios.delete(`/asset-categories/${id}`);

      if (response.data?.success) {
        await fetchCategories();
      }
    } catch (err) {
      console.error(err);
      alert(
        err?.response?.data?.message || "Failed to delete hardware category."
      );
    }
  };

  const handleToggleStatus = async (category) => {
    try {
      const response = await axios.put(`/asset-categories/${category.id}`, {
        ...category,
        is_active: !category.is_active,
      });

      if (response.data?.success) {
        await fetchCategories();
      }
    } catch (err) {
      console.error(err);
      alert("Failed to update category status.");
    }
  };

  const addField = () => {
    const value = newField.trim();

    if (!value) return;

    setNewCategory((previous) => ({
      ...previous,
      fields: [...previous.fields, value],
    }));

    setNewField("");
  };

  const removeField = (index) => {
    setNewCategory((previous) => ({
      ...previous,
      fields: previous.fields.filter((_, i) => i !== index),
    }));
  };

  const totalCategories = categories.length;
  const activeCategories = categories.filter(
    (category) => Boolean(category.is_active)
  ).length;
  const inactiveCategories = totalCategories - activeCategories;

  // Show software categories in the second tab.
  if (activeTab === "software") {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">
              Asset & Software Categories
            </h3>
            <p className="text-sm text-muted-foreground">
              Configure classification for hardware assets and software
              licences.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap border-b border-border">
          <button
            type="button"
            onClick={() => setActiveTab("hardware")}
            className="flex items-center gap-2 border-b-2 border-transparent px-5 py-3 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            <Icon name="Laptop" size={18} />
            Hardware Asset Types ({totalCategories})
          </button>

          <button
            type="button"
            onClick={() => setActiveTab("software")}
            className="flex items-center gap-2 border-b-2 border-primary px-5 py-3 text-sm font-medium text-primary"
          >
            <Icon name="KeyRound" size={18} />
            Software Categories
          </button>
        </div>

        <SoftwareCategoryTab />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page heading */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">
            Asset & Software Categories
          </h3>
          <p className="text-sm text-muted-foreground">
            Configure classification and dynamic fields for physical hardware
            and software licences.
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground">Total Classifications</p>
          <p className="mt-2 text-3xl font-semibold text-card-foreground">
            {totalCategories}
          </p>
        </div>

        <div className="rounded-lg border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground">Hardware Types</p>
          <p className="mt-2 text-3xl font-semibold text-card-foreground">
            {totalCategories}
          </p>
        </div>

        <div className="rounded-lg border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground">Software Categories</p>
          <p className="mt-2 text-3xl font-semibold text-card-foreground">
            —
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Managed in the Software Categories tab
          </p>
        </div>

        <div className="rounded-lg border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground">Active Taxonomy</p>
          <p className="mt-2 text-3xl font-semibold text-card-foreground">
            {totalCategories
              ? `${Math.round((activeCategories / totalCategories) * 100)}%`
              : "0%"}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {inactiveCategories} inactive hardware type
            {inactiveCategories === 1 ? "" : "s"}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap border-b border-border">
        <button
          type="button"
          onClick={() => setActiveTab("hardware")}
          className="flex items-center gap-2 border-b-2 border-primary px-5 py-3 text-sm font-medium text-primary"
        >
          <Icon name="Laptop" size={18} />
          Hardware Asset Types ({totalCategories})
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("software")}
          className="flex items-center gap-2 border-b-2 border-transparent px-5 py-3 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <Icon name="KeyRound" size={18} />
          Software Categories
        </button>
      </div>

      {/* Hardware categories */}
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="font-semibold text-card-foreground">
              Hardware Asset Types
            </h4>
            <p className="text-sm text-muted-foreground">
              Manage physical asset classifications and custom fields.
            </p>
          </div>

          <Button
            variant="default"
            onClick={() => setShowAddModal(true)}
            iconName="Plus"
            iconPosition="left"
          >
            Add Category
          </Button>
        </div>

        {error && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-error/30 bg-error/5 p-4 text-sm text-error">
            <span>{error}</span>
            <button
              type="button"
              onClick={fetchCategories}
              className="font-medium underline"
            >
              Retry
            </button>
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-border bg-card">
          {loading ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              Loading hardware categories...
            </div>
          ) : categories.length === 0 ? (
            <div className="p-10 text-center">
              <Icon
                name="FolderOpen"
                size={30}
                className="mx-auto mb-3 text-muted-foreground"
              />
              <p className="font-medium text-card-foreground">
                No hardware categories found
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Add a category to get started.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="border-b border-border bg-muted/40">
                  <tr>
                    <th className="px-5 py-3 font-medium text-muted-foreground">
                      Category
                    </th>
                    <th className="px-5 py-3 font-medium text-muted-foreground">
                      Code
                    </th>
                    <th className="px-5 py-3 font-medium text-muted-foreground">
                      Linked Assets
                    </th>
                    <th className="px-5 py-3 font-medium text-muted-foreground">
                      Custom Fields
                    </th>
                    <th className="px-5 py-3 font-medium text-muted-foreground">
                      Status
                    </th>
                    <th className="px-5 py-3 text-right font-medium text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-border">
                  {categories.map((category) => (
                    <tr key={category.id} className="hover:bg-muted/20">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <Icon name="Laptop" size={19} />
                          <div>
                            <p className="font-medium text-card-foreground">
                              {category.name}
                            </p>
                            {category.description && (
                              <p className="mt-1 max-w-xs truncate text-xs text-muted-foreground">
                                {category.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4 font-medium text-card-foreground">
                        {category.code || "—"}
                      </td>

                      <td className="px-5 py-4 text-card-foreground">
                        {category.assetCount ?? category.asset_count ?? 0}
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex flex-wrap gap-1">
                          {Array.isArray(category.fields) &&
                          category.fields.length ? (
                            category.fields.map((field, index) => (
                              <span
                                key={`${category.id}-${index}`}
                                className="rounded bg-muted px-2 py-1 text-xs text-muted-foreground"
                              >
                                {typeof field === "string"
                                  ? field
                                  : field?.name || "Field"}
                              </span>
                            ))
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </div>
                      </td>

                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                            category.is_active
                              ? "bg-success/10 text-success"
                              : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {category.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditCategory(category)}
                            iconName="Edit"
                          >
                            Edit
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(category)}
                            iconName={category.is_active ? "Pause" : "Play"}
                          >
                            {category.is_active ? "Deactivate" : "Activate"}
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCategory(category.id)}
                            iconName="Trash2"
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/edit hardware category modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border p-6">
              <h3 className="text-lg font-semibold text-card-foreground">
                {editingCategory ? "Edit Hardware Category" : "Add Hardware Category"}
              </h3>
              <Button variant="ghost" size="icon" onClick={closeModal}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            <div className="space-y-6 p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Input
                  label="Category Name"
                  type="text"
                  value={newCategory.name}
                  onChange={(event) =>
                    setNewCategory((previous) => ({
                      ...previous,
                      name: event.target.value,
                    }))
                  }
                  placeholder="e.g. Mobile Devices"
                  required
                />

                <Input
                  label="Category Code"
                  type="text"
                  value={newCategory.code}
                  onChange={(event) =>
                    setNewCategory((previous) => ({
                      ...previous,
                      code: event.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="e.g. MOB"
                  required
                />
              </div>

              <Input
                label="Description"
                type="text"
                value={newCategory.description}
                onChange={(event) =>
                  setNewCategory((previous) => ({
                    ...previous,
                    description: event.target.value,
                  }))
                }
                placeholder="Describe this hardware category"
              />

              <div>
                <label className="mb-2 block text-sm font-medium text-card-foreground">
                  Custom Fields
                </label>

                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={newField}
                    onChange={(event) => setNewField(event.target.value)}
                    placeholder="Enter custom field name"
                  />
                  <Button
                    variant="outline"
                    onClick={addField}
                    disabled={!newField.trim()}
                    iconName="Plus"
                  >
                    Add
                  </Button>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {newCategory.fields.map((field, index) => (
                    <span
                      key={`${field}-${index}`}
                      className="flex items-center gap-2 rounded bg-muted px-3 py-2 text-sm"
                    >
                      {field}
                      <button
                        type="button"
                        onClick={() => removeField(index)}
                        aria-label={`Remove ${field}`}
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <Checkbox
                label="Active Category"
                checked={newCategory.is_active}
                onChange={(value) =>
                  setNewCategory((previous) => ({
                    ...previous,
                    is_active:
                      typeof value === "boolean"
                        ? value
                        : Boolean(value?.target?.checked),
                  }))
                }
              />
            </div>

            <div className="flex justify-end gap-3 border-t border-border p-6">
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={
                  editingCategory ? handleUpdateCategory : handleAddCategory
                }
                disabled={!newCategory.name.trim() || !newCategory.code.trim()}
              >
                {editingCategory ? "Update Category" : "Create Category"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetCategoriesTab;
